import {
	HttpClient,
	HttpResponse
}                             from '@angular/common/http';
import { Injectable }         from '@angular/core';
import { AbstractApiService } from '@app/core/http/abstract-api.service';
import { SanitizerService }   from '@app/core/http/sanitizer.service';
import { TransitionService }  from '@app/core/http/transition.service';
import {
	ITssHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                             from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }      from '@app/core/models/transition.model';
import {
	TssExternal,
	TssExternalStatusEnum
}                             from '@app/modules/tss/models/tss.model';
import { LoggerService }      from '@app/shared/services/logger.service';
import { MapperService }      from '@http/mapper.service';
import { IHttpContextConfig } from '@interceptors/interceptors.model';
import {
	FeatureScopesEnum,
	TssExternalScopesEnum
}                             from '@models/scope.enums';
import {
	CreateExtTssRequestParams,
	DeleteExtTssRequestParams,
	ExternalTssApiClient,
	ExtVirtualTssBodyDTO,
	ExtVirtualTssDTO,
	ExtVirtualTssStatusEnumDTO,
	GetExtAllTssRequestParams,
	GetExtTssByBusinessPremisesRequestParams,
	GetExtTssRequestParams,
	UpdateExtTssRequestParams,
	VirtualTssDeviceTypeEnumDTO
}                             from '@openApiGenerated/management-api';
import {
	Observable,
	of
}                             from 'rxjs';
import { delay }              from 'rxjs/operators';

@Injectable( {
	providedIn: 'root'
} )
export class TssExternalApiService extends AbstractApiService {

	constructor(
		private readonly _externalTssApiClient: ExternalTssApiClient,
		private readonly _mapperService: MapperService,
		private readonly _sanitize: SanitizerService,
		private readonly _transition: TransitionService,
		protected http: HttpClient,
		protected logger: LoggerService
	) {
		super();
		this.http = http;
	}

	// SANITIZER ######################################
	private sanitizeApiResponse( entity: TssExternal, context = this ): void {
		if ( entity instanceof TssExternal ) {
			entity.createdDate = context._sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.deviceType = context._mapperService.tss.deviceTypeDtoToTmo( entity.deviceType as unknown as VirtualTssDeviceTypeEnumDTO );
			entity.lastModifiedDate = context._sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );
			entity.status = context._mapperService.tss.statusExternalDtoToTmo( entity?.status as unknown as ExtVirtualTssStatusEnumDTO );
		}
	}

	// LIST TYPE REQUESTS #############################
	public list(
		options?: {
			httpContextConfig: IHttpContextConfig,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<TssExternal>> {
		const {
			httpContextConfig,
			throwError = false
		} = options;

		this.logger.debug( 'pre-list call' );
		const requestParam: GetExtAllTssRequestParams = {
			sort: 'lastModifiedDate,desc'
		};

		const req$ = this._externalTssApiClient.getExtAllTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtVirtualTssDTO, TssExternal>(
			req$,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public search(
		options: {
			enablePermissionCheck?: boolean,
			httpContextConfig?: IHttpContextConfig,
			params: ITssHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<TssExternal>> {
		const {
			enablePermissionCheck = true,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-search call: ', params );

		if ( enablePermissionCheck && !this.hasAllPermissions( [ FeatureScopesEnum.ERIC, TssExternalScopesEnum.List ] ) ) {
			return of(
				this._transition.getEmptyRequestCompleteListState<TssExternal>( RequestActionEnum.LIST, RequestState.FAILED_REQUEST_PRE_VALIDATION )
			).pipe( delay( 100 ) );
		}

		const requestParam: GetExtAllTssRequestParams = {
			businessPremisesIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.businessPremisesIds ),
			filter: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue( params?.filter ),
			organizationIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.organizationIds ),
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
			sort: params?.sort,
			status: this._mapperService.tss.statusListExternalTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as TssExternalStatusEnum )
			),
			tssIds: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.tssIds )
		};

		const req$ = this._externalTssApiClient.getExtAllTss(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtVirtualTssDTO, TssExternal>(
			req$,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listByBPremisesId(
		options: {
			businessPremisesId: string,
			enablePermissionCheck?: boolean,
			httpContextConfig?: IHttpContextConfig,
			params?: ITssHttpParams,
			throwError?: boolean
		}
	): Observable<RequestCompleteListState<TssExternal>> {
		const {
			businessPremisesId,
			enablePermissionCheck = true,
			httpContextConfig,
			params,
			throwError = false
		} = options;

		this.logger.debug( 'pre-listByBPremisesId call: ', businessPremisesId, params );

		if ( enablePermissionCheck && !this.hasAllPermissions( [ FeatureScopesEnum.ERIC, TssExternalScopesEnum.List ] ) ) {
			return of(
				this._transition.getEmptyRequestCompleteListState<TssExternal>( RequestActionEnum.LIST, RequestState.FAILED_REQUEST_PRE_VALIDATION )
			).pipe( delay( 100 ) );
		}

		const requestParam: GetExtTssByBusinessPremisesRequestParams = {
			businessPremisesId,
			status: this._mapperService.tss.statusListExternalTmoToDto(
				this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status as unknown as TssExternalStatusEnum )
			),
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			sort: params?.sort
		};

		const req$ = this._externalTssApiClient.getExtTssByBusinessPremises(
			requestParam,
			'body',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleListRequestResponse<ExtVirtualTssDTO, TssExternal>(
			req$,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	// ENTITY TYPE REQUESTS ###########################

	public confirmFinalTssLogExport( tss: TssExternal ): Observable<RequestCompleteEntityState<TssExternal>> {
		this.logger.debug( 'pre-confirmFinalTssLogExport call: ', tss );
		return of( this._transition.getEmptyRequestCompleteEntityState<TssExternal>( RequestActionEnum.CONFIRM, RequestState.NOT_SUPPORTED ) );
	}

	public create(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tss: TssExternal,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<TssExternal>> {
		const {
			httpContextConfig,
			throwError = false,
			tss,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-create call: ', { ...tss } );

		const extVirtualTssBodyDTO: ExtVirtualTssBodyDTO = {
			bsiId: tss.bsiId,
			deviceType: this._mapperService.tss.deviceTypeTmoToDto( tss.deviceType ),
			name: tss.name,
			status: this._mapperService.tss.statusExternalTmoToDto( tss?.status ),
			tssSerialNumber: tss.tssSerialNumber
		};

		const requestParam: CreateExtTssRequestParams = {
			businessPremisesId: tss.businessPremisesId,
			extVirtualTssBodyDTO
		};

		const req$ = this._externalTssApiClient.createExtTss(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtVirtualTssDTO | HttpResponse<ExtVirtualTssDTO>, TssExternal>(
			req$,
			RequestActionEnum.CREATE,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tssId: string
		}
	): Observable<RequestCompleteEntityState<TssExternal>> {
		const {
			httpContextConfig,
			throwError = false,
			tssId
		} = options;

		this.logger.debug( 'pre-delete call: ', tssId );
		const requestParam: DeleteExtTssRequestParams = {
			tssId
		};

		const req$ = this._externalTssApiClient.deleteExtTss(
			requestParam,
			'response',
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<HttpResponse<any>, TssExternal>(
			req$,
			RequestActionEnum.DELETE,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}


	public read(
		options: {
			enableRequestPreValidation?: boolean,
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tssId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<TssExternal>> {
		const {
			enableRequestPreValidation,
			httpContextConfig,
			throwError = false,
			tssId,
			withFullResponse = false
		} = options;

		const requestAction = RequestActionEnum.READ;

		this.logger.debug( 'pre-read call: ', tssId );

		if ( !tssId ) {

			const requestState: RequestState = enableRequestPreValidation
																				 ? RequestState.FAILED_REQUEST_PRE_VALIDATION
																				 : null;

			return of( this._transition.getEmptyRequestCompleteEntityState<TssExternal>( requestAction, requestState ) );
		}

		const requestParam: GetExtTssRequestParams = {
			tssId
		};

		const req$ = this._externalTssApiClient.getExtTss(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtVirtualTssDTO | HttpResponse<ExtVirtualTssDTO>, TssExternal>(
			req$,
			requestAction,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}


	public triggerErsSync( tss: TssExternal ): Observable<RequestCompleteEntityState<TssExternal>> {
		this.logger.debug( 'pre-triggerSync call: ', tss );
		return of( this._transition.getEmptyRequestCompleteEntityState<TssExternal>( RequestActionEnum.SYNC, RequestState.NOT_SUPPORTED ) );
	}

	public triggerUpload( tss: TssExternal ): Observable<RequestCompleteEntityState<TssExternal>> {
		this.logger.debug( 'pre-triggerUpload call: ', tss );
		return of( this._transition.getEmptyRequestCompleteEntityState<TssExternal>( RequestActionEnum.UPLOAD, RequestState.NOT_SUPPORTED ) );
	}

	public update(
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			tss: TssExternal,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<TssExternal>> {
		const {
			httpContextConfig,
			throwError = false,
			tss,
			withFullResponse = false
		} = options;

		this.logger.debug( 'pre-update call: ', { ...tss } );
		const extVirtualTssBodyDTO: ExtVirtualTssBodyDTO = {
			bsiId: tss.bsiId,
			deviceType: this._mapperService.tss.deviceTypeTmoToDto( tss.deviceType ),
			name: tss.name,
			status: this._mapperService.tss.statusExternalTmoToDto( tss?.status ),
			tssSerialNumber: tss.tssSerialNumber
		};

		const requestParam: UpdateExtTssRequestParams = {
			tssId: tss.id,
			ifMatch: tss.etag,
			extVirtualTssBodyDTO
		};

		const req$ = this._externalTssApiClient.updateExtTss(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this._transition.handleEntityRequestResponse<ExtVirtualTssDTO | HttpResponse<ExtVirtualTssDTO>, TssExternal>(
			req$,
			RequestActionEnum.UPDATE,
			TssExternal,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
